<template>
    <div class="sky">
        <!-- Current background -->
        <div
            class="gradient-layer"
            :class="{ active: currentLayer === 1 }"
            :style="{ background: gradientStyle1 }"
        ></div>

        <!-- Next background -->
        <div
            class="gradient-layer"
            :class="{ active: currentLayer === 2 }"
            :style="{ background: gradientStyle2 }"
        ></div>

        <!-- Subtle darkening for readability -->
        <div class="dark-overlay"></div>
    </div>
</template>

<script>
import { EventBus } from '../eventBus.js';

export default {
    data() {
        return {
            imageUrl: null,

            gradientStyle1: this.defaultGradient(),
            gradientStyle2: this.defaultGradient(),

            currentLayer: 1,

            transitionTimer: null
        };
    },

    watch: {
        imageUrl(newUrl) {
            if (newUrl) {
                this.extractColorsAndCreateGradient(newUrl);
            } else {
                this.updateGradient(this.defaultGradient());
            }
        }
    },

    mounted() {
        EventBus.on('update-background', this.handleBackgroundUpdate);
        EventBus.on('update-background-home', this.handleHomeBackgroundUpdate);
    },

    beforeUnmount() {
        EventBus.off('update-background', this.handleBackgroundUpdate);
        EventBus.off('update-background-home', this.handleHomeBackgroundUpdate);

        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
        }
    },

    methods: {
        handleBackgroundUpdate(newImageUrl) {
            if (!newImageUrl || newImageUrl === this.imageUrl) {
                return;
            }
            this.imageUrl = newImageUrl;
        },

        handleHomeBackgroundUpdate(newImageUrl) {
            if (this.imageUrl) {
                return;
            }

            if (newImageUrl) {
                this.imageUrl = newImageUrl;
            }
        },

        defaultGradient() {
            return `
                radial-gradient(
                    circle at 20% 20%,
                    rgba(35, 45, 65, 0.8),
                    transparent 55%
                ),
                radial-gradient(
                    circle at 80% 25%,
                    rgba(45, 35, 60, 0.8),
                    transparent 55%
                ),
                radial-gradient(
                    circle at 50% 85%,
                    rgba(25, 40, 50, 0.8),
                    transparent 60%
                ),
                #101216
            `;
        },

        updateGradient(newGradient) {
            // Put the new gradient on the hidden layer.
            if (this.currentLayer === 1) {
                this.gradientStyle2 = newGradient;

                // Wait one frame so the browser registers the
                // new background before starting the fade.
                requestAnimationFrame(() => {
                    this.currentLayer = 2;
                });
            } else {
                this.gradientStyle1 = newGradient;

                requestAnimationFrame(() => {
                    this.currentLayer = 1;
                });
            }
        },

        async extractColorsAndCreateGradient(imageUrl) {
            const img = new Image();

            img.crossOrigin = 'Anonymous';

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d', {
                        willReadFrequently: true
                    });

                    const size = 120;

                    canvas.width = size;
                    canvas.height = size;

                    ctx.drawImage(img, 0, 0, size, size);

                    const imageData = ctx.getImageData(
                        0,
                        0,
                        size,
                        size
                    );

                    const colors = this.extractDominantColors(
                        imageData.data,
                        6
                    );

                    if (!colors.length) {
                        this.updateGradient(this.defaultGradient());
                        return;
                    }

                    const processedColors = colors.map(color => {
                        return this.prepareColor(color);
                    });

                    const gradient = this.createAmbientGradient(
                        processedColors
                    );

                    this.updateGradient(gradient);
                } catch (error) {
                    console.error(
                        'Error extracting colors:',
                        error
                    );

                    this.updateGradient(
                        this.defaultGradient()
                    );
                }
            };

            img.onerror = () => {
                console.error(
                    'Failed to load background image'
                );

                this.updateGradient(
                    this.defaultGradient()
                );
            };

            img.src = imageUrl;
        },

        extractDominantColors(pixelData, numColors) {
            const colorMap = {};

            // Sample every 4th pixel.
            for (let i = 0; i < pixelData.length; i += 16) {
                const r = pixelData[i];
                const g = pixelData[i + 1];
                const b = pixelData[i + 2];
                const a = pixelData[i + 3];

                if (a < 128) {
                    continue;
                }

                // Ignore almost-black pixels.
                // They are usually background portions of album art
                // and make the ambient background unnecessarily dark.
                const brightness =
                    (r + g + b) / 3;

                if (brightness < 20) {
                    continue;
                }

                // Slightly finer quantization than before.
                const quantizedR =
                    Math.round(r / 24) * 24;

                const quantizedG =
                    Math.round(g / 24) * 24;

                const quantizedB =
                    Math.round(b / 24) * 24;

                const key =
                    `${quantizedR},${quantizedG},${quantizedB}`;

                colorMap[key] =
                    (colorMap[key] || 0) + 1;
            }

            return Object.entries(colorMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, numColors)
                .map(([rgb]) => {
                    const [r, g, b] =
                        rgb.split(',').map(Number);

                    return {
                        r: Math.min(255, r),
                        g: Math.min(255, g),
                        b: Math.min(255, b)
                    };
                });
        },

        prepareColor(color) {
            // Darken moderately.
            // We want the actual album colors to remain recognizable.
            let r = color.r * 0.62;
            let g = color.g * 0.62;
            let b = color.b * 0.62;

            // Slightly increase saturation.
            const average = (r + g + b) / 3;

            const saturation = 1.18;

            r = average + (r - average) * saturation;
            g = average + (g - average) * saturation;
            b = average + (b - average) * saturation;

            // Prevent completely black / excessively bright colors.
            r = Math.max(18, Math.min(180, r));
            g = Math.max(18, Math.min(180, g));
            b = Math.max(18, Math.min(180, b));

            return {
                r: Math.round(r),
                g: Math.round(g),
                b: Math.round(b)
            };
        },

        rgba(color, alpha) {
            return `rgba(
                ${color.r},
                ${color.g},
                ${color.b},
                ${alpha}
            )`;
        },

        createAmbientGradient(colors) {
            /*
             * Instead of:
             *
             * linear-gradient(color1, color2, color3...)
             *
             * create several large overlapping color fields.
             *
             * This spreads the album colors across the entire
             * screen and avoids visible boundaries.
             */

            const positions = [
                ['18%', '18%'],
                ['82%', '18%'],
                ['50%', '45%'],
                ['15%', '78%'],
                ['85%', '78%'],
                ['50%', '100%']
            ];

            const gradients = [];

            colors.forEach((color, index) => {
                const position =
                    positions[index % positions.length];

                gradients.push(`
                    radial-gradient(
                        circle at ${position[0]} ${position[1]},
                        ${this.rgba(color, 0.78)} 0%,
                        ${this.rgba(color, 0.45)} 25%,
                        ${this.rgba(color, 0.18)} 50%,
                        transparent 72%
                    )
                `);
            });

            // Dark base underneath everything.
            gradients.push('#101216');

            return gradients.join(', ');
        }
    }
};
</script>

<style scoped>
.sky {
    position: fixed;
    inset: 0;

    width: 100vw;
    height: 100vh;

    overflow: hidden;

    background: #101216;
}

/*
 * Both layers occupy the entire screen.
 *
 * We crossfade between them instead of changing the visible
 * background directly.
 */
.gradient-layer {
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    opacity: 0;

    transition:
        opacity 1.4s cubic-bezier(0.4, 0, 0.2, 1);

    will-change: opacity;

    transform: scale(1.02);
}

.gradient-layer.active {
    opacity: 1;
}

/*
 * Subtle dark layer.
 *
 * This is intentionally low-opacity.
 * The colors should remain visible.
 */
.dark-overlay {
    position: absolute;
    inset: 0;

    z-index: 10;

    background:
        linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.08) 0%,
            rgba(0, 0, 0, 0.18) 100%
        );

    pointer-events: none;
}
</style>